import React from 'react';
import { styled } from '@compiled/react';
// @ts-expect-error - TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'SortOrderType'. Did you mean to use 'import SortOrderType from "@atlaskit/dynamic-table"' instead?
import type { SortOrderType } from '@atlaskit/dynamic-table';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import type { IntlShape } from '@atlassian/jira-intl/src/index.tsx';
import { FormattedRelativeV2 as FormattedRelative } from '@atlassian/jira-intl/src/v2/formatted-relative.tsx';
import type { OrderBy } from '@atlassian/jira-servicedesk-knowledge-services/src/services/rest/articles/index.tsx';
import type { Knowledge } from '@atlassian/jira-servicedesk-knowledge-services/src/types.tsx';
import Link from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/knowledge-hub-spa-link/index.tsx';
import messages from '../messages.tsx';
import type { KnowledgeTableColumn, KnowledgeTableContext } from '../types.tsx';

export const getSortKey = (columns: KnowledgeTableColumn[], orderBy: OrderBy): string | undefined =>
	columns.find((column) => column.sortKey === orderBy.slice(1))?.key;

export const getSortOrder = (orderBy: OrderBy): SortOrderType | undefined =>
	orderBy[0] === '-' ? 'DESC' : 'ASC';

export const getSortBy = (
	columns: KnowledgeTableColumn[],
	key: string,
	sortOrder: string,
): OrderBy => {
	const sortKey = columns.find((column) => column.key === key)?.sortKey;
	switch (sortKey) {
		case 'featured':
			return sortOrder === 'DESC' ? '-featured' : '+featured';
		case 'lastModified':
			return sortOrder === 'DESC' ? '-lastModified' : '+lastModified';
		case 'title':
			return sortOrder === 'DESC' ? '-title' : '+title';
		default:
			// prettier-ignore
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			(sortKey as undefined);
			return '-lastModified';
	}
};

export const createTitleColumn = (
	getLink: (knowledge: Knowledge, context: KnowledgeTableContext) => string,
): KnowledgeTableColumn => ({
	key: 'knowledge-title',
	sortKey: 'title',
	header: (intl: IntlShape) => ({
		content: intl.formatMessage(messages.titleHeader),
		isSortable: true,
		shouldTruncate: true,
		width: `${ARTICLE_COLUMN_WIDTH}px`,
	}),
	row: (intl: IntlShape, index: number, knowledge: Knowledge, context: KnowledgeTableContext) => (
		<CellPadding>
			<Link to={getLink(knowledge, context)}>{knowledge.title}</Link>
		</CellPadding>
	),
});

export const viewCountColumn: KnowledgeTableColumn = {
	key: 'knowledge-view-count',
	header: (intl: IntlShape) => ({
		content: <LeftAlignedText>{intl.formatMessage(messages.viewCountHeader)}</LeftAlignedText>,
		isSortable: false,
		width: `${VIEWS_COLUMN_WIDTH}px`,
	}),
	row: (intl: IntlShape, index: number, knowledge: Knowledge) => (
		<LeftAlignedText>{knowledge.viewCount || 0}</LeftAlignedText>
	),
};

export const lastModifiedColumn: KnowledgeTableColumn = {
	key: 'knowledge-last-modified',
	sortKey: 'lastModified',
	header: (intl: IntlShape) => ({
		content: intl.formatMessage(messages.lastModifiedHeader),
		isSortable: true,
		width: `${LAST_MODIFIED_COLUMN_WIDTH}px`,
	}),
	row: (intl: IntlShape, index: number, knowledge: Knowledge) => (
		<LeftAlignedText>
			<Tooltip tag="span" content={knowledge.lastModified}>
				<FormattedRelative value={knowledge.lastModified} />
			</Tooltip>
		</LeftAlignedText>
	),
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const LeftAlignedText = styled.div({
	textAlign: 'left',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const CellPadding = styled.div({
	paddingTop: token('space.100'),
	paddingRight: 0,
	paddingBottom: token('space.100'),
	paddingLeft: 0,
});

export const ARTICLE_COLUMN_WIDTH = gridSize * 44;
export const VIEWS_COLUMN_WIDTH = gridSize * 8;
export const LAST_MODIFIED_COLUMN_WIDTH = gridSize * 16;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const RightAlignedText = styled.div({
	textAlign: 'right',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const CenterAlignedText = styled.div({
	textAlign: 'center',
});
